
import React, { useMemo } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { SectionBlockConfiguration } from '@/types/block-configurations';
import { DragGhostPlaceholder } from '../DragGhostPlaceholder';
import { BlockRenderer } from '../BlockRenderer';
import { useDragState } from '../DndProvider';

export function SectionBlock({
  block,
  isSelected,
  isHovered,
  onSelect,
  onHover,
}: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as SectionBlockConfiguration;

  // Sort child blocks by orderIndex (matches rendering order from BlockRenderer)
  const sortedChildBlocks = useMemo(() => {
    const sorted = [...(block.childBlocks || [])].sort((a, b) => a.orderIndex - b.orderIndex);
    return sorted;
  }, [block.childBlocks]);

  const hasChildren = sortedChildBlocks.length > 0;
  const { dropTarget, activeData } = useDragState();

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onHover(block.id);
      }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-section cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        backgroundColor: config.background?.color || 'transparent',
        backgroundImage: config.background?.imageUrl ? `url(${config.background.imageUrl})` : undefined,
        backgroundSize: config.background?.imageSize || 'cover',
        backgroundRepeat: config.background?.imageRepeat || 'no-repeat',
        paddingTop: `${config.padding?.top ?? 0}px`,
        paddingRight: `${config.padding?.right ?? 0}px`,
        paddingBottom: `${config.padding?.bottom ?? 0}px`,
        paddingLeft: `${config.padding?.left ?? 0}px`,
        borderRadius: `${config.border?.radius || 0}px`,
        borderWidth: config.border?.width ? `${config.border.width}px` : '0',
        borderStyle: config.border?.style || 'solid',
        borderColor: config.border?.color || '#000000',
        maxWidth: config.maxWidth ? `${config.maxWidth}px` : undefined,
        width: '100%',
        direction: config.direction || 'ltr',
      }}
    >
      {hasChildren ? (
        // Sections only contain Columns and Groups — always render as horizontal flex
        // No gap — MJML columns sit flush; spacing comes from column padding
        <div className="lw-block-section-columns flex flex-wrap w-full">
          {sortedChildBlocks.map((childBlock, idx) => (
            <React.Fragment key={childBlock.id}>
              {dropTarget?.parentId === block.id && dropTarget.index === idx && (
                <DragGhostPlaceholder orientation="vertical" />
              )}
              <BlockRenderer block={childBlock} parentId={block.id} index={idx} depth={1} />
            </React.Fragment>
          ))}
          {dropTarget?.parentId === block.id && dropTarget.index === sortedChildBlocks.length && (
            <DragGhostPlaceholder orientation="vertical" />
          )}
        </div>
      ) : (
        // Empty state — sections only accept Columns and Groups
        <div className={`rounded-lg border-2 border-dashed flex items-center justify-center min-h-16 ${
          activeData ? 'border-primary/40 bg-primary/5' : 'border-border/60'
        }`}>
          {dropTarget?.parentId === block.id ? (
            <DragGhostPlaceholder orientation="horizontal" />
          ) : (
            <p className={`text-sm font-medium select-none ${
              activeData ? 'text-primary/60' : 'text-muted-foreground/50'
            }`}>
              Drop columns here to build layouts
            </p>
          )}
        </div>
      )}
    </div>
  );
}
